import { useState } from "react";
import { Link2, FileText, Video, DollarSign, CheckCircle2, Upload, ExternalLink, Plus, X, FolderOpen, Lock, PenLine } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { derivePayment, fmtUSD, quoteHasDocusign, quoteDocusignSigned } from "./quoteMapping";

const PAYMENT_METHODS = ["Wire", "Card", "ACH", "Other"];

function isValidUrl(string) {
  try { new URL(string); return true; }
  catch (_) { return false; }
}

function parseLinks(text) {
  return text.split('\n').map(l => l.trim()).filter(l => l && isValidUrl(l));
}

// Reps paste a share link and often skip the title box. Name it from the host
// so a missing title never blocks the link from being saved.
function deriveLinkTitle(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (host.includes("drive.google") || host.includes("docs.google")) return "Google Drive Files";
    if (host.includes("dropbox")) return "Dropbox Files";
    if (host.includes("wetransfer")) return "WeTransfer Files";
    if (host.includes("sharepoint") || host.includes("onedrive")) return "OneDrive Files";
    if (host.includes("box.com")) return "Box Files";
    return host;
  } catch (_) {
    return "Project Files";
  }
}

// Returns the {title, url} entry a draft row would become, or null if there's
// nothing usable to save.
function buildFileLink(draft) {
  const url = (draft?.url || "").trim();
  if (!url || !isValidUrl(url)) return null;
  return { title: (draft.title || "").trim() || deriveLinkTitle(url), url };
}

export default function IntakeScopeStep({ form, setForm, errors, onNext, onBack, quoteMode = false, sourceQuote = null }) {
  const [agreementTab, setAgreementTab] = useState(form.agreement_type || "link");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [newFileLink, setNewFileLink] = useState({ title: "", url: "" });
  const pay = quoteMode ? derivePayment(sourceQuote) : null;
  const hasDocusign = quoteMode && quoteHasDocusign(sourceQuote);

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      // For now, store file name - actual upload will be handled on submit
      setUploadedFileName(file.name);
      setForm({ 
        ...form, 
        agreement_type: "upload",
        agreement_file: file,
        signed_agreement_link: "" // Clear link when uploading
      });
    }
  }
  
  function handleTabChange(tab) {
    setAgreementTab(tab);
    setForm({ ...form, agreement_type: tab });
    if (tab === "link") {
      setUploadedFileName("");
    }
  }
  
  // Project File Links management
  function addFileLink() {
    const entry = buildFileLink(newFileLink);
    if (!entry) return;
    setForm(prev => ({ ...prev, project_file_links: [...(prev.project_file_links || []), entry] }));
    setNewFileLink({ title: "", url: "" });
  }

  function removeFileLink(index) {
    setForm(prev => ({
      ...prev,
      project_file_links: (prev.project_file_links || []).filter((_, i) => i !== index)
    }));
  }

  // A URL typed into the draft row but never "+"-ed used to be discarded when
  // the rep hit Next: the draft lives in local state and only the committed
  // list is submitted. The rep saw their link on screen, submitted, and the PM
  // got an empty Operational Links panel. Commit the draft on the way out.
  function commitDraftThen(next) {
    if (buildFileLink(newFileLink)) addFileLink();
    next();
  }

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <Link2 className="w-5 h-5" />
          Links & Scope
        </CardTitle>
        <CardDescription className="text-sm">Add agreement, recordings, and scope</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
        {/* Agreement Section - Upload OR Link */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Signed Service Agreement *</Label>
          
          <Tabs value={agreementTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className={`grid w-full ${hasDocusign ? "grid-cols-3" : "grid-cols-2"}`}>
              {hasDocusign && (
                <TabsTrigger value="docusign" className="text-xs sm:text-sm">
                  <PenLine className="w-3.5 h-3.5 mr-1.5" />
                  DocuSign
                </TabsTrigger>
              )}
              <TabsTrigger value="link" className="text-xs sm:text-sm">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Paste Link
              </TabsTrigger>
              <TabsTrigger value="upload" className="text-xs sm:text-sm">
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Upload File
              </TabsTrigger>
            </TabsList>
            
            {hasDocusign && (
              <TabsContent value="docusign" className="mt-3">
                <div className="flex items-start gap-2 rounded-lg border p-3">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">
                      {quoteDocusignSigned(sourceQuote)
                        ? "Signed in DocuSign — HQ attaches the PDF automatically"
                        : "Sent through DocuSign — HQ attaches the signed PDF automatically"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Nothing to paste or upload. If the client hasn't finished signing yet, the PM can pull it later from the project's Agreement tab.
                    </p>
                  </div>
                </div>
              </TabsContent>
            )}

            <TabsContent value="link" className="mt-3">
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={form.signed_agreement_link}
                  onChange={e => setForm({ ...form, signed_agreement_link: e.target.value })}
                  placeholder="https://dropbox.com/... or any URL"
                  className={`pl-10 h-10 sm:h-11 ${errors.signed_agreement_link ? "border-red-500" : ""}`}
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Supports Dropbox, Google Drive, LeadConnector, or any download link
              </p>
            </TabsContent>
            
            <TabsContent value="upload" className="mt-3">
              <div className="space-y-3">
                <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    id="agreement-upload"
                  />
                  <label htmlFor="agreement-upload" className="cursor-pointer">
                    {uploadedFileName ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-sm">{uploadedFileName}</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium">Click to upload PDF</p>
                        <p className="text-xs text-muted-foreground mt-1">PDF preferred, DOC/DOCX accepted</p>
                      </div>
                    )}
                  </label>
                </div>
                {uploadedFileName && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setUploadedFileName("");
                      setForm({ ...form, agreement_file: null });
                    }}
                    className="text-xs text-muted-foreground"
                  >
                    Remove file
                  </Button>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          {errors.signed_agreement_link && agreementTab === "link" && (
            <p className="text-xs text-red-500">{errors.signed_agreement_link}</p>
          )}
        </div>
        
        {/* Fathom Links */}
        <div className="space-y-1.5">
          <Label className="text-sm">Fathom Recording Links</Label>
          <p className="text-xs text-muted-foreground">One link per line</p>
          <div className="relative">
            <Video className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
            <Textarea
              value={form.fathom_links_text}
              onChange={e => setForm({ ...form, fathom_links_text: e.target.value })}
              placeholder={"https://fathom.video/..."}
              rows={2}
              className="pl-10 text-sm"
            />
          </div>
          {form.fathom_links_text && parseLinks(form.fathom_links_text).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {parseLinks(form.fathom_links_text).map((_, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  Fathom #{i + 1}
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        {/* Project File Links - NEW Structured Field */}
        <div className="space-y-3">
          <Label className="text-sm font-medium flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Project File Links
          </Label>
          <p className="text-xs text-muted-foreground">Add links to architectural files, references, etc.</p>
          
          {/* Existing links */}
          {(form.project_file_links || []).length > 0 && (
            <div className="space-y-2">
              {(form.project_file_links || []).map((link, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-secondary/30 rounded-md" data-testid={`project-file-link-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{link.title}</p>
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline truncate block"
                    >
                      {link.url}
                    </a>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFileLink(idx)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add new link */}
          <div className="flex gap-2">
            <Input
              value={newFileLink.title}
              onChange={e => setNewFileLink({ ...newFileLink, title: e.target.value })}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addFileLink(); } }}
              placeholder="Link title (optional)"
              className="flex-1 h-9 text-sm"
            />
            <Input
              value={newFileLink.url}
              onChange={e => setNewFileLink({ ...newFileLink, url: e.target.value })}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addFileLink(); } }}
              placeholder="https://drive.google.com/..."
              className="flex-[2] h-9 text-sm"
              data-testid="project-file-link-url-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addFileLink}
              disabled={!buildFileLink(newFileLink)}
              className="h-9"
              data-testid="add-project-file-link-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {newFileLink.url.trim() && !isValidUrl(newFileLink.url) && (
            <p className="text-xs text-red-500" data-testid="project-file-link-url-error">
              That doesn't look like a full URL — include https:// so it saves.
            </p>
          )}
        </div>
        
        {/* Vertical Safe Zone */}
        <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
          <div>
            <Label className="text-sm">Vertical Safe Zone?</Label>
            <p className="text-xs text-muted-foreground">For social media ratios</p>
          </div>
          <Switch
            checked={form.vertical_safe_zone_required}
            onCheckedChange={v => setForm({ ...form, vertical_safe_zone_required: v })}
          />
        </div>
        
        {/* Scope Details */}
        <div className="space-y-1.5">
          <Label className="text-sm">Scope of Work</Label>
          <Textarea
            value={form.scope_details}
            onChange={e => setForm({ ...form, scope_details: e.target.value })}
            placeholder="Describe deliverables and requirements..."
            rows={3}
            className="text-sm"
          />
        </div>
        
        {/* Payment Section */}
        {quoteMode && pay ? (
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center gap-2">
              <Label className="text-base font-semibold">Payment</Label>
              <Badge variant="secondary" className="text-[10px] font-normal gap-1"><Lock className="w-2.5 h-2.5" /> from quote</Badge>
            </div>

            {/* Payment method is the ONLY thing the rep chooses here. */}
            <div className="space-y-1.5 max-w-xs">
              <Label className="text-xs sm:text-sm">Method</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="h-9 sm:h-10 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Read-only pulled schedule */}
            <div className="rounded-lg border divide-y">
              {pay.milestones.map((m, i) => (
                <div key={i} className="flex items-start justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{m.label}</p>
                    <p className="text-xs text-muted-foreground">{m.note}</p>
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap">{fmtUSD(m.amount)}</span>
                </div>
              ))}
              {pay.monthly > 0 && pay.term > 0 && (
                <div className="flex items-start justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Platform Subscription</p>
                    <p className="text-xs text-muted-foreground">{pay.term}-month term</p>
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap">{fmtUSD(pay.monthly)}/mo</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-secondary/40">
                <span className="text-sm font-semibold">Contract Total{pay.monthly > 0 && pay.term > 0 ? " (incl. subscription)" : ""}</span>
                <span className="text-sm font-bold">{fmtUSD(pay.grandTotal)}</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Pulled from the quote's payment schedule. Amounts aren't editable here — change them in the Client Calculator.
            </p>
          </div>
        ) : (
          <div className="border-t pt-4 space-y-4">
            <Label className="text-base font-semibold">Payment</Label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Method</Label>
                <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="h-9 sm:h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Contract Value *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    type="number"
                    value={form.contract_value || ""}
                    onChange={e => setForm({ ...form, contract_value: e.target.value })}
                    placeholder="0"
                    className="pl-7 h-9 sm:h-10 text-sm"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">Total contract amount</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Expected Deposit</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    type="number"
                    value={form.expected_deposit || ""}
                    onChange={e => setForm({ ...form, expected_deposit: e.target.value })}
                    placeholder="0"
                    className="pl-7 h-9 sm:h-10 text-sm"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">Amount to collect upfront</p>
              </div>
            </div>

            {/* Validation warning */}
            {form.expected_deposit && form.contract_value &&
             parseFloat(form.expected_deposit) > parseFloat(form.contract_value) && (
              <p className="text-xs text-red-500">
                Expected deposit cannot exceed contract value
              </p>
            )}

            <div className="space-y-1.5">
              <Label className="text-sm">Payment Terms</Label>
              <Input
                value={form.payment_terms}
                onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                placeholder="e.g., 50% upfront, 50% on delivery"
                className="h-10 text-sm"
              />
            </div>
          </div>
        )}
        
        {/* Internal Notes */}
        <div className="space-y-1.5">
          <Label className="text-sm">Internal Notes</Label>
          <Textarea
            value={form.internal_notes}
            onChange={e => setForm({ ...form, internal_notes: e.target.value })}
            placeholder="Notes for the Ops team..."
            rows={2}
            className="text-sm"
          />
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row justify-between gap-2 px-4 sm:px-6 pb-4 sm:pb-6">
        <Button variant="outline" onClick={() => commitDraftThen(onBack)} className="w-full sm:w-auto h-10 sm:h-11">Back</Button>
        <Button onClick={() => commitDraftThen(onNext)} className="w-full sm:w-auto h-10 sm:h-11">Next: Review</Button>
      </CardFooter>
    </Card>
  );
}
